import React from "react";
import { useAppSelector } from "../../../hooks/hooks";

// redux getters
import {
  selectPropertyReport,
  selectPropertyReportMonitoringDifferences,
  selectPropertyReportStatus,
} from "../../../features/reports/reportsSlice";

// utils
import { formatPhoneNumber } from "../../../utils";

// types

// components
import {
  Container,
  ContainerLineItem,
  Padding,
  Text,
  Table,
  Layout,
} from "@kidslivesafe/sc-react";

import { PreForclosureDetails } from "../../../features/reports/reportTypes";
import { generateList } from "@kidslivesafe/sc-foundation";
import LoadingContainerLineItem from "../../../components/UIComponents/LoadingContainerLineItem/LoadingContainerLineItem";

const PreForeclosureDetails: React.FC<{
  containerId: string;
  printReady?: boolean;
}> = ({ containerId, printReady }) => {
  const pulledReport = useAppSelector(selectPropertyReport);

  const propertyReportStatus = useAppSelector(selectPropertyReportStatus);
  const reportDifferences = useAppSelector(
    selectPropertyReportMonitoringDifferences,
  );

  const loading = propertyReportStatus === "loading";

  function checkifNA(value: string) {
    if (value === "N/A") {
      return true;
    } else {
      return false;
    }
  }

  function getBorrowerNames(details: PreForclosureDetails) {
    let borrowNames: string[] = [];
    let borrowerOne = "";
    let borrowerTwo = "";

    if (!checkifNA(details.borrower1FirstNameAndMiddleName)) {
      borrowerOne = details.borrower1FirstNameAndMiddleName;
    }

    if (!checkifNA(details.borrower1LastNameOrCorporationName)) {
      borrowerOne += " " + details.borrower1LastNameOrCorporationName;
    }
    if (!checkifNA(details.borrower2FirstNameAndMiddleName)) {
      borrowerTwo = details.borrower2FirstNameAndMiddleName;
    }
    if (!checkifNA(details.borrower2LastNameOrCorporationName)) {
      borrowerTwo += " " + details.borrower2LastNameOrCorporationName;
    }
    if (borrowerOne !== "") {
      borrowNames.push(borrowerOne);
    }
    if (borrowerTwo !== "") {
      borrowNames.push(borrowerTwo);
    }

    return borrowNames.join(", ");
  }

  return (
    <>
      {pulledReport && pulledReport.preforeclosureDetails ? (
        <Padding
          left="md"
          right="md"
          breakpointStyles={{
            desktop: {
              space: "none",
            },
          }}
        >
          <Container
            containerTitle="Pre-foreclosure Information"
            containerId={containerId}
            newInfo={
              reportDifferences?.hasChanges &&
              reportDifferences?.preforeclosures
            }
          >
            <Layout type="flex" flexProps={{ direction: "column" }}>
              {loading ? (
                <LoadingContainerLineItem
                  lineItemHeader
                  lineItemSubHeader
                  numberToLoad={1}
                />
              ) : (
                <>
                  <ContainerLineItem
                    lineItemHeader={pulledReport.preforeclosureDetails.pfcType}
                    lineItemSubHeader={generateList(
                      [
                        {
                          value:
                            pulledReport.preforeclosureDetails.pastDueAmount,
                          label: "Past Due: ",
                        },
                        {
                          value: pulledReport.preforeclosureDetails.dueDate,
                          label: "Due Date: ",
                        },
                      ],
                      "|",
                    )}
                  />
                  <Table
                    tableDataObject={{
                      "": (
                        <Layout type="flex" flexProps={{ direction: "column" }}>
                          <Text size="sm" fontWeight="bold">
                            Recording Date:{" "}
                            <Text tag="span" size="sm" fontWeight="normal">
                              {pulledReport.preforeclosureDetails.recordingDate}
                            </Text>
                          </Text>
                          <Text size="sm" fontWeight="bold">
                            Principal Owed:{" "}
                            <Text tag="span" size="sm" fontWeight="normal">
                              {pulledReport.preforeclosureDetails.unpaidBalance}
                            </Text>
                          </Text>
                        </Layout>
                      ),
                      "Loan Information": (
                        <Layout type="flex" flexProps={{ direction: "column" }}>
                          <Text size="sm" fontWeight="bold">
                            Loan Recording Date:{" "}
                            <Text tag="span" size="sm" fontWeight="normal">
                              {
                                pulledReport.preforeclosureDetails
                                  .loanRecordingDate
                              }
                            </Text>
                          </Text>
                          <Text size="sm" fontWeight="bold">
                            Original Loan Value:{" "}
                            <Text tag="span" size="sm" fontWeight="normal">
                              {
                                pulledReport.preforeclosureDetails
                                  .originalLoanAmount
                              }
                            </Text>
                          </Text>
                          <Text size="sm" fontWeight="bold">
                            Lender Name:{" "}
                            <Text tag="span" size="sm" fontWeight="normal">
                              {
                                pulledReport.preforeclosureDetails
                                  .currentLenderName
                              }
                            </Text>
                          </Text>
                          <Text size="sm" fontWeight="bold">
                            Title Company:{" "}
                            <Text tag="span" size="sm" fontWeight="normal">
                              {pulledReport.preforeclosureDetails.titleCompany}
                            </Text>
                          </Text>
                          <Text size="sm" fontWeight="bold">
                            Borrowers:{" "}
                            <Text tag="span" size="sm" fontWeight="normal">
                              {getBorrowerNames(
                                pulledReport.preforeclosureDetails,
                              )}
                            </Text>
                          </Text>
                        </Layout>
                      ),
                      "Trustee Information": (
                        <Layout type="flex" flexProps={{ direction: "column" }}>
                          <Text size="sm" fontWeight="bold">
                            Trustee Name:{" "}
                            <Text tag="span" size="sm" fontWeight="normal">
                              {
                                pulledReport.preforeclosureDetails
                                  .trusteeCareOfName
                              }
                            </Text>
                          </Text>
                          <Text size="sm" fontWeight="bold">
                            Trustee Phone:{" "}
                            <Text tag="span" size="sm" fontWeight="normal">
                              {formatPhoneNumber(
                                pulledReport.preforeclosureDetails
                                  .trusteeTelephoneNumber,
                              )}
                            </Text>
                          </Text>
                        </Layout>
                      ),
                      "Auction Information": (
                        <Layout type="flex" flexProps={{ direction: "column" }}>
                          <Text size="sm" fontWeight="bold">
                            Auction Address:{" "}
                            <Text tag="span" size="sm" fontWeight="normal">
                              {
                                pulledReport.preforeclosureDetails
                                  .auctionLocation
                              }
                              {", "}
                              {pulledReport.preforeclosureDetails.auctionCity}
                            </Text>
                          </Text>
                          <Text size="sm" fontWeight="bold">
                            Auction Date:{" "}
                            <Text tag="span" size="sm" fontWeight="normal">
                              {pulledReport.preforeclosureDetails.auctionDate}
                            </Text>
                          </Text>
                          <Text size="sm" fontWeight="bold">
                            Auction Time:{" "}
                            <Text tag="span" size="sm" fontWeight="normal">
                              {pulledReport.preforeclosureDetails.auctionTime}
                            </Text>
                          </Text>
                          <Text size="sm" fontWeight="bold">
                            Auction Min. Bid:{" "}
                            <Text tag="span" size="sm" fontWeight="normal">
                              {
                                pulledReport.preforeclosureDetails
                                  .auctionMinimumBidAmount
                              }
                            </Text>
                          </Text>
                        </Layout>
                      ),
                    }}
                    labelGap="xxxs"
                    orderOfProperties={[]}
                  />
                </>
              )}
            </Layout>
          </Container>
        </Padding>
      ) : null}
    </>
  );
};

export default PreForeclosureDetails;
